import {RawAxiosRequestConfig} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {AuthApi, SMSApi, SortColumn, SortDirection, SystemApi} from "components/src/api/admin/generated";
import {AuthenticatedRoute} from "components/src/auth/AuthenticatedRoute";
import {Login} from "components/src/auth/Login";
import {Success} from "components/src/auth/Success";
import {Tfa} from "components/src/auth/Tfa";
import {Ca} from "components/src/ca/Ca";
import {Certificates} from "components/src/certificate/Certificates";
import {ErrorPage} from "components/src/common/ErrorPage";
import {AuthenticatedAdminContextProvider} from "components/src/context/AuthenticatedAdminContextProvider";
import {AuthenticatedUserContextProvider} from "components/src/context/AuthenticatedUserContextProvider";
import {AvailablePropertiesContextProvider} from "components/src/context/AvailablePropertiesContextProvider";
import {EffectivePermissionsContextProvider} from "components/src/context/EffectivePermissionsContextProvider";
import {EnterpriseEditionContextProvider} from "components/src/context/EnterpriseEditionContextProvider";
import {SpinnerStatusContextProvider} from "components/src/context/SpinnerStatusContextProvider";
import {CRLs} from "components/src/crl/CRLs";
import {Dashboard} from "components/src/dashboard/Dashboard";
import {DLPPatterns} from "components/src/dlp/DLPPatterns"
import {DLPSystemSelection} from "components/src/dlp/DLPSystemSelection";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {AppLayout} from "components/src/layout/AppLayout";
import {LoginLayout} from "components/src/layout/LoginLayout";
import {LogView} from "components/src/log/LogView";
import {MPAQueue} from "components/src/mpa/MPAQueue";
import {MPARepository} from "components/src/mpa/MPARepository";
import {MTAMaps} from "components/src/mta/MTAMaps";
import {MTAQueue} from "components/src/mta/MTAQueue";
import {MTASettings} from "components/src/mta/MTASettings";
import {Domains} from "components/src/other/Domains";
import {PGPKeyRing} from "components/src/pgp/PGPKeyRing"
import {QuarantineQueue} from "components/src/quarantine/QuarantineQueue";
import {Settings} from "components/src/settings/Settings";
import {Shell} from "components/src/shell/Shell";
import {SMSQueue, SMSQueueOperations} from "components/src/sms/SMSQueue";
import {SMSSendMessagePopup} from "components/src/sms/SMSSendMessagePopup";
import React, {ComponentType} from 'react'
import {createPortal} from "react-dom";
import {createBrowserRouter, createRoutesFromElements, Route, RouterProvider} from "react-router-dom";
import {AppMenu} from "./AppMenu";

interface AppProps {
    Sidebar : ComponentType<{
        className?: string;
    }>
    OptionalSidePanel? : ComponentType<{
        className?: string;
    }>
    children? : React.ReactNode;
}

/**
 * REST api for accessing the back-end
 */
const authApi = new AuthApi(ApiConfig.config());
const systemApi = new SystemApi(ApiConfig.config());
const smsApi = new SMSApi(ApiConfig.config());

/**
 * SMS operations for the SMS queue, backed by the admin SMS API.
 */
const smsOperations: SMSQueueOperations = {
    getSMSQueueSize: () => smsApi.sMSControllerGetSMSQueueSize().then(r => r.data),
    getSMSMessages: (firstResult, maxResults) =>
        smsApi.sMSControllerGetSMSMessages(SortColumn.Created, SortDirection.Asc, firstResult, maxResults)
            .then(r => r.data),
    deleteSMS: ids => smsApi.sMSControllerDeleteSMS(ids).then(() => undefined)
};

export const App = ({Sidebar, OptionalSidePanel, children} : AppProps) =>
{
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const validateOTPFunc = async (totp: string, options?: RawAxiosRequestConfig) => {
        return await authApi.authControllerValidateTOTP({otp : totp}, options);
    };

    const router = createBrowserRouter(
        createRoutesFromElements(
            <>
                {/* some pages should be accessible without authentication */}
                <Route path="/"  errorElement={<ErrorPage/>} element={
                    <LoginLayout/>
                }>
                    <Route path="login" element={
                        <Login/>
                    } />
                </Route>

                <Route path={"tfa"} element={
                    <Tfa validateOTPFunc={validateOTPFunc}/>
                } />

                <Route path="/" errorElement={<ErrorPage/>}  element={
                    // all child "pages" (except login) will be wrapped by the following context providers
                    // and will have the AdminLayout as the parent component
                    // because the AdminLayout is wrapped with an AuthenticatedRoute, all access to the admin
                    // pages requires authentication
                    <SpinnerStatusContextProvider>
                        <AuthenticatedUserContextProvider>
                            <AuthenticatedAdminContextProvider>
                                <AvailablePropertiesContextProvider>
                                    <EffectivePermissionsContextProvider>
                                        <EnterpriseEditionContextProvider>
                                            <AuthenticatedRoute>
                                                <AppLayout AppSidebar={Sidebar}
                                                           Menu={AppMenu}
                                                           OptionalSidePanel={OptionalSidePanel}
                                                           fetchSessionTimeout={() => systemApi.systemControllerGetSessionTimeout().then(r => r.data * 1000)}
                                                           keepSessionAlive={() => systemApi.systemControllerGetSessionTimeout().catch(() => {})}/>
                                            </AuthenticatedRoute>
                                        </EnterpriseEditionContextProvider>
                                    </EffectivePermissionsContextProvider>
                                </AvailablePropertiesContextProvider>
                            </AuthenticatedAdminContextProvider>
                        </AuthenticatedUserContextProvider>
                    </SpinnerStatusContextProvider>
                }>
                    {/* The below child routes are wrapped by the AdminLayout and require authentication */}
                    <Route index element={
                        <Dashboard/>
                    } />
                    <Route path={"success"} element={
                        <Success/>
                    } />
                    <Route path={"domains/*"} element={
                        <Domains/>
                    } />
                    <Route path={"settings/system/*"} element={
                        <Settings/>
                    } />
                    <Route path={"certificates"} element={
                        <Certificates/>
                    } />
                    <Route path={"crls"} element={
                        <CRLs/>
                    } />
                    <Route path={"ca"} element={
                        <Ca/>
                    } />
                    <Route path={"pgp"} element={
                        <PGPKeyRing/>
                    } />
                    <Route path={"dlp/patterns"} element={
                        <DLPPatterns/>
                    } />
                    <Route path={"dlp/selection"} element={
                        <DLPSystemSelection/>
                    } />
                    <Route path={"dlp/quarantine"} element={
                        <QuarantineQueue/>
                    } />
                    <Route path={"mta/settings"} element={
                        <MTASettings/>
                    } />
                    <Route path={"mta/queue/mta"} element={
                        <MTAQueue/>
                    } />
                    <Route path={"mta/maps"} element={
                        <MTAMaps/>
                    } />
                    <Route path={"mta/queue/mpa"} element={
                        <MPAQueue/>
                    } />
                    <Route path={"mta/queue/repository"} element={
                        <MPARepository/>
                    } />
                    <Route path={"log"} element={
                        <LogView/>
                    } />
                    <Route path={"sms"} element={
                        <SMSQueue operations={smsOperations} sendDialog={SMSSendMessagePopup}/>
                    } />
                    <Route path={"shell"} element={
                        <Shell/>
                    } />
                    {children}
                </Route>
            </>
        )
    );
    return (
        <React.StrictMode>
            {createPortal(
                <>
                    <link rel="icon" href={t(['favicon'])} />
                    <title>{t(["application.title"])}</title>
                </>,
                document.head
            )}

            <RouterProvider router={router} />
        </React.StrictMode>
    )
}
